import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { C, F } from './UI';
import { NotificationBell, LanguageSwitcher } from './Addons';
import NotificationListener from './NotificationListener';
import ImpersonationBanner from './ImpersonationBanner';
import API from '../api/axios';
import toast from 'react-hot-toast';

const FB = "'Bebas Neue',sans-serif";
const FM = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const ORANGE = '#334155';
function useShellFonts() {
  useEffect(() => {
    if (document.getElementById('hm-shell-fonts')) return;
    const l = document.createElement('link');
    l.id = 'hm-shell-fonts'; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(l);
  }, []);
}

/* ── Clean SVG Icons ──────────────────────────── */
const Icon = {
  grid: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="10" y="1.5" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="1.5" y="10" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="10" y="10" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  inbox: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M2 10h3l1.5 2.5h4L12 10h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="1.5" y="2.5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  file: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M9.5 1.5H3.5A1.5 1.5 0 002 3v11a1.5 1.5 0 001.5 1.5h10A1.5 1.5 0 0015 14V7L9.5 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M9.5 1.5V7H15" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M5.5 10.5h6M5.5 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  machine: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1.5" y="6" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M11.5 9.5H15V13h-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="4" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="8.5" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M3 6V4a2 2 0 012-2h3a2 2 0 012 2v2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  users: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <circle cx="6.5" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M1.5 14.5c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M11.5 7.5a2 2 0 100-4M15.5 14.5c0-2.21-1.79-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  building: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="2" y="2" width="13" height="13.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5.5 15.5V11h6v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <rect x="5" y="4.5" width="2.5" height="2.5" rx=".5" stroke="currentColor" strokeWidth="1.2"/>
      <rect x="9.5" y="4.5" width="2.5" height="2.5" rx=".5" stroke="currentColor" strokeWidth="1.2"/>
      <rect x="5" y="7.5" width="2.5" height="2.5" rx=".5" stroke="currentColor" strokeWidth="1.2"/>
      <rect x="9.5" y="7.5" width="2.5" height="2.5" rx=".5" stroke="currentColor" strokeWidth="1.2"/>
    </svg>
  ),
  quote: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M3 2h7l4 4v8.5A1.5 1.5 0 0112.5 16h-9A1.5 1.5 0 012 14.5v-11A1.5 1.5 0 013.5 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M10 2v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M5 9.5h7M5 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="13" cy="11.5" r="2" stroke="currentColor" strokeWidth="1.3" fill="#fff"/>
      <path d="M12.4 11.5l.5.5 1-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  signout: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 14H3a1.5 1.5 0 01-1.5-1.5v-9A1.5 1.5 0 013 2h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M10.5 11L14 8l-3.5-3M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  support: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <circle cx="8.5" cy="8.5" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="8.5" cy="8.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3.6 3.6l2.2 2.2M11.2 11.2l2.2 2.2M13.4 3.6l-2.2 2.2M5.8 11.2l-2.2 2.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  money: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1.5" y="3.5" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="8.5" cy="8.5" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M4.5 8.5h.5M12 8.5h.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

const NAV = {
  customer: [
    { to: '/customer',              icon: 'grid',    label: 'nav.dashboard',  exact: true },
  ],
  supplier: [
    { to: '/supplier',             icon: 'grid',    label: 'nav.dashboard',  exact: true },
  ],
  admin: [
    { to: '/admin',              icon: 'grid',     label: 'Overview',          exact: true },
    { to: '/admin/suppliers',    icon: 'building', label: 'Suppliers' },
    { to: '/admin/users',        icon: 'users',    label: 'Users' },
    { to: '/admin/work-orders',  icon: 'file',     label: 'Work Orders' },
    { to: '/admin/trust',        icon: 'building', label: 'Trust Moderation' },
    { to: '/admin/support',      icon: 'support',  label: 'Support Console' },
  ],
};

const ROLE_TAG = { customer: 'Customer Portal', supplier: 'Supplier Portal', admin: 'Admin Console', site_supervisor: 'Site Logbook' };

export default function Layout({ role }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const links = NAV[role] || [];
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    (NAV[role] || []).forEach(l => { if (l.group && l.match && location.pathname.startsWith(l.match)) init[l.group] = true; });
    return init;
  });
  useEffect(() => {
    setOpenGroups(g => {
      const next = { ...g };
      let changed = false;
      (NAV[role] || []).forEach(l => {
        if (!l.group || !l.match) return;
        const inGroup = location.pathname.startsWith(l.match);
        if (inGroup && !next[l.group]) { next[l.group] = true; changed = true; }
        else if (!inGroup && next[l.group]) { next[l.group] = false; changed = true; }
      });
      return changed ? next : g;
    });
  }, [location.pathname, role]);
  const accent = ORANGE;
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const menuRef = useRef(null);
  useShellFonts();

  // Poll total unread chat messages so the sidebar shows a badge
  // without needing to open the chat page.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = () => API.get('/messages/contacts')
      .then(r => {
        if (!alive) return;
        const total = (r.data.data || []).reduce((s, c) => s + (Number(c.unread) || 0), 0);
        setChatUnread(total);
      })
      .catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  function doLogout() { logout(); navigate('/'); }

  const [navOpen, setNavOpen] = useState(false);
  // close drawer when route changes
  useEffect(() => { setNavOpen(false); }, [navigate]);


  return (
    /* Fully fluid shell: fills big screens, reflows on small ones (no fixed
       floor — below 900px the sidebar becomes a drawer and rows stack via the
       global 760px rules). Public pages (landing, auth) aren't wrapped. */
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg }}>
      <ImpersonationBanner />
    <div className="hm-shell" style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', background: C.bg }}>
      <style>{`
        .hm-burger{display:none}
        .hm-backdrop{display:none}
        @media (max-width: 900px){
          .hm-side{position:fixed;left:0;top:0;bottom:0;transform:translateX(-100%);transition:transform .22s ease;box-shadow:0 14px 40px rgba(0,0,0,.4)}
          .hm-side.is-open{transform:translateX(0)}
          .hm-backdrop{display:block;position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:90;opacity:0;pointer-events:none;transition:opacity .2s}
          .hm-backdrop.is-open{opacity:1;pointer-events:auto}
          .hm-burger{display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:8px;border:1px solid ${C.border};background:#fff;cursor:pointer;flex-shrink:0;margin-right:6px}
          .hm-topbar{padding:0 14px !important}
          .hm-welcome{font-size:10px !important;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:42vw}
          .hm-welcome-name{font-size:12px !important}
          .hm-content{padding:18px 14px !important}
          .hm-post-cta{padding:8px 12px !important;font-size:10px !important;letter-spacing:.06em !important}
          .hm-post-cta-label{display:none}
        }

        @media (max-width: 560px){
          .hm-content{padding:14px 10px !important}
          .hm-topbar{padding:0 10px !important;gap:6px}
        }
      `}</style>

      {/* ── Mobile backdrop ─────────────────────── */}
      {role === 'admin' && <div className={`hm-backdrop${navOpen ? ' is-open' : ''}`} onClick={() => setNavOpen(false)} />}

      {/* ── Sidebar ─────────────────────────────── */}
      {role === 'admin' && <aside className={`hm-side${navOpen ? ' is-open' : ''}`} style={{
        width: 196, flexShrink: 0, background: '#060606',
        borderRight: '1px solid #2e2e2e',
        display: 'flex', flexDirection: 'column',
        height: '100%', zIndex: 100,
      }}>
        {/* Logo */}
        <div style={{ padding: '26px 22px 20px', borderBottom: '1px solid #2e2e2e' }}>
          <div style={{ fontFamily: FB, fontSize: 26, color: '#f0ede8', letterSpacing: '.06em', lineHeight: 1 }}>
            Heavy<span style={{ color: accent }}>Mach</span>
          </div>
          <div style={{ fontFamily: FM, fontSize: 10, color: '#7a7a7a', marginTop: 7, textTransform: 'uppercase', letterSpacing: '.18em' }}>
            {ROLE_TAG[role] || 'Portal'}
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '14px 0', overflowY: 'auto' }}>
          {links.map(link => link.group ? (
            <div key={link.group}>
              <button
                onClick={() => setOpenGroups(g => ({ ...g, [link.group]: !g[link.group] }))}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 13,
                  padding: '12px 22px', marginBottom: 1, background: 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontFamily: F, fontSize: 13,
                  fontWeight: location.pathname.startsWith(link.match) ? 600 : 400,
                  letterSpacing: '.02em', textAlign: 'left',
                  color: location.pathname.startsWith(link.match) ? '#f0ede8' : '#7a7a7a',
                  borderLeft: `2px solid ${location.pathname.startsWith(link.match) ? accent : 'transparent'}`,
                }}>
                <span style={{ flexShrink: 0, opacity: .85 }}>{Icon[link.icon]}</span>
                <span style={{ flex: 1 }}>{link.label}</span>
                <span style={{ fontSize: 10, opacity: .7, transform: openGroups[link.group] ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
              </button>
              {openGroups[link.group] && link.children.map(c => (
                <NavLink key={c.to} to={c.to} end
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 22px 9px 52px', marginBottom: 1,
                    fontFamily: F, fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                    letterSpacing: '.02em',
                    color: isActive ? '#f0ede8' : '#7a7a7a',
                    background: isActive ? '#1a1a1a' : 'transparent',
                    textDecoration: 'none',
                    borderLeft: `2px solid ${isActive ? accent : 'transparent'}`,
                  })}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'currentColor', opacity: .5 }} />
                  {c.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <NavLink key={link.to} to={link.to} end={link.exact}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 13,
                padding: '12px 22px', marginBottom: 1,
                fontFamily: F, fontSize: 13, fontWeight: isActive ? 600 : 400,
                letterSpacing: '.02em',
                color: isActive ? '#f0ede8' : '#7a7a7a',
                background: isActive ? '#1a1a1a' : 'transparent',
                textDecoration: 'none', transition: 'color .15s, background .15s',
                borderLeft: `2px solid ${isActive ? accent : 'transparent'}`,
              })}>
              <span style={{ flexShrink: 0, opacity: .85, position: 'relative' }}>
                {Icon[link.icon]}
                {link.icon === 'chat' && chatUnread > 0 && (
                  <span style={{
                    position: 'absolute', top: -5, right: -6,
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#25D366', border: '2px solid #060606',
                  }} />
                )}
              </span>
              <span style={{ flex: 1 }}>
                {link.label?.startsWith('nav.') ? t(link.label) : link.label}
              </span>
              {link.icon === 'chat' && chatUnread > 0 && (
                <span style={{
                  background: '#25D366', color: '#fff', fontFamily: F,
                  fontSize: 11, fontWeight: 700, minWidth: 20, height: 20,
                  padding: '0 6px', borderRadius: 10, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}>
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>}


      {/* ── Main ─────────────────────────────────── */}
      <main style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top bar */}
        <header className="hm-topbar" style={{
          background: '#fff', borderBottom: `1px solid ${C.border}`,
          padding: '0 32px', height: 44, flexShrink: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', zIndex: 50, gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {(role === 'supplier' || role === 'customer') && (
              <div style={{ fontFamily: FB, fontSize: 22, color: '#0f172a', letterSpacing: '.06em', lineHeight: 1, flexShrink: 0, marginRight: 6 }}>
                Heavy<span style={{ color: '#f97316' }}>Mach</span>
              </div>
            )}
            {role === 'admin' && <button className="hm-burger" aria-label="Open menu" onClick={() => setNavOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke={C.ink} strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>}
            {role === 'admin' && (
            <div className="hm-welcome" style={{ fontFamily: FM, fontSize: 11, color: C.muted, letterSpacing: '.1em', textTransform: 'uppercase', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span>{t('common.welcome')},</span>
              <span className="hm-welcome-dot" aria-hidden="true" />
              <span className="hm-welcome-name">{user?.company?.trim() || user?.name}</span>
              <style>{`
                .hm-welcome-name{
                  font-family:${FM}; font-size:15px; font-weight:700;
                  letter-spacing:.05em; text-transform:uppercase;
                  background:linear-gradient(90deg,#0f172a 0%,#f97316 20%,#0f172a 40%,#7c2d12 60%,#f97316 80%,#0f172a 100%);
                  background-size:300% 100%;
                  -webkit-background-clip:text; -webkit-text-fill-color:transparent;
                  background-clip:text;
                  animation:hm-welcome-shimmer 6s linear infinite;
                  white-space:nowrap;
                }
                .hm-welcome-dot{
                  display:inline-block; width:8px; height:8px; border-radius:50%;
                  background:#10b981; flex-shrink:0;
                  box-shadow:0 0 0 0 rgba(16,185,129,.7);
                  animation:hm-welcome-pulse 1.8s ease-out infinite;
                }
                @keyframes hm-welcome-shimmer{
                  0%{background-position:0% 50%}
                  100%{background-position:300% 50%}
                }
                @keyframes hm-welcome-pulse{
                  0%{box-shadow:0 0 0 0 rgba(16,185,129,.7); transform:scale(1)}
                  70%{box-shadow:0 0 0 8px rgba(16,185,129,0); transform:scale(1.08)}
                  100%{box-shadow:0 0 0 0 rgba(16,185,129,0); transform:scale(1)}
                }
                .hm-welcome-name:hover{ animation-duration:2s; }
              `}</style>
            </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <NotificationBell />
            {role === 'customer' && (
              <NavLink to="/customer?post=requirement" className="hm-post-cta" style={{
                position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 9,
                background: 'linear-gradient(135deg,#1e293b 0%,#334155 50%,#7c2d12 100%)',
                color: '#fff', padding: '10px 20px 10px 16px',
                borderRadius: 10, fontFamily: FM, fontSize: 11, fontWeight: 500,
                letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none',
                boxShadow: '0 4px 14px rgba(51,65,85,.35), 0 0 0 1px rgba(249,115,22,.25), inset 0 1px 0 rgba(255,255,255,.12)',
                overflow: 'hidden', isolation: 'isolate',
                transition: 'transform .18s cubic-bezier(.4,0,.2,1), box-shadow .18s',
              }}>
                <style>{`
                  .hm-post-cta::before{content:'';position:absolute;inset:0;z-index:-1;
                    background:linear-gradient(120deg,transparent 30%,rgba(249,115,22,.28) 50%,transparent 70%);
                    transform:translateX(-130%);transition:transform .6s ease;}
                  .hm-post-cta:hover{transform:translateY(-2px);
                    box-shadow:0 8px 22px rgba(249,115,22,.35), 0 0 0 1px rgba(249,115,22,.55), inset 0 1px 0 rgba(255,255,255,.18);}
                  .hm-post-cta:hover::before{transform:translateX(130%);}
                  .hm-post-cta:active{transform:translateY(0);}
                  .hm-post-cta svg{transition:transform .25s cubic-bezier(.4,0,.2,1);}
                  .hm-post-cta:hover svg{transform:rotate(90deg);}
                `}</style>
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#f97316,#ea580c)',
                  boxShadow: '0 0 10px rgba(249,115,22,.6)',
                  flexShrink: 0,
                }}>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M6 1.5v9M1.5 6h9" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
                <span className="hm-post-cta-label">{t('nav.post_enquiry')}</span>
              </NavLink>
            )}
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                title={user?.name}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                style={{
                  width: 34, height: 34, borderRadius: '50%', background: accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FB, fontSize: 16, color: '#fff',
                  overflow: 'hidden', border: 'none', padding: 0, cursor: 'pointer',
                  boxShadow: menuOpen ? `0 0 0 3px ${accent}33` : 'none',
                  transition: 'box-shadow .15s',
                }}>
                {user?.avatar
                  ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (user?.name || 'U')[0].toUpperCase()}
              </button>

              {menuOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 12px)', right: 0,
                  width: 268, background: '#fff', borderRadius: 14,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 12px 40px rgba(0,0,0,.16)',
                  overflow: 'hidden', zIndex: 200,
                  animation: 'hmMenuIn .14s ease-out',
                }}>
                  <style>{`@keyframes hmMenuIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>

                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 18px 16px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: '50%', background: accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: FB, fontSize: 19, color: '#fff', overflow: 'hidden', flexShrink: 0,
                    }}>
                      {user?.avatar
                        ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (user?.name || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: F, fontSize: 14, fontWeight: 600, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user?.name || 'User'}
                      </div>
                      <div style={{ fontFamily: FM, fontSize: 11, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 3 }}>
                        {user?.email}
                      </div>
                    </div>
                  </div>

                  {/* Settings + Sign out */}
                  <div style={{ padding: '8px 0' }}>
                    <button onClick={() => { setMenuOpen(false); navigate(`/${role}/settings`); }} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 13,
                      padding: '11px 18px', background: 'transparent', border: 'none',
                      cursor: 'pointer', fontFamily: F, fontSize: 13.5, color: C.ink, textAlign: 'left',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f4f4f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ flexShrink: 0, opacity: .7, fontSize: 15, width: 17, textAlign: 'center' }}>⚙</span>
                      Settings
                    </button>
                    <button onClick={() => { setMenuOpen(false); doLogout(); }} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 13,
                      padding: '11px 18px', background: 'transparent', border: 'none',
                      cursor: 'pointer', fontFamily: F, fontSize: 13.5, color: '#dc2626', textAlign: 'left',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ flexShrink: 0, opacity: .85, display: 'flex' }}>{Icon.signout}</span>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="hm-content" style={{
          flex: 1, minHeight: 0, width: '100%', boxSizing: 'border-box',
          overflowY: location.pathname.endsWith('/workspace/x') ? 'hidden' : 'scroll',
          scrollbarGutter: location.pathname.endsWith('/workspace/x') ? 'auto' : 'stable both-edges',
          padding: location.pathname.endsWith('/workspace/x') ? '18px 0 0' : location.pathname.includes('/workspace/') ? '0 18px 28px' : '28px 18px',
          display: location.pathname.endsWith('/workspace/x') ? 'flex' : 'block',
          flexDirection: 'column',
        }}>
          <Outlet />
        </div>

      </main>
      {/* Global poller for unread enquiries / quotes / work-orders */}
      <NotificationListener enabled={!!user && role !== 'admin'} />
      </div>
    </div>
  );
}
